'use client'

import { useMemo } from 'react'

import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { theme } from 'tailwind.config.js'

import { Loader } from '@infura/packages.ui/Loader'

import { PopperPortal } from 'components/dashboard/key/KeysList/PopperPortal'
import { TooltipContent } from 'components/dashboard/key/KeysList/TooltipContent'

import { useRequestHealth } from 'hooks/useRequestHealth'

const green = theme.extend.colors.success.default.DEFAULT
const red = theme.extend.colors.error.default.DEFAULT

const BAR_HEIGHT = 16
const BAR_WIDTH = 175
const MARGIN = { bottom: 5, left: 0, right: 0, top: 5 }

type TableRequestHealthProps = {
  id: string
}

export type TransformedDataType = Array<{
  date: string
  success: number
  failures: number
  total: number
  '400': number
  '500': number
}>

export const TableRequestHealth = ({ id }: TableRequestHealthProps) => {
  const { dataTyped, isLoading } = useRequestHealth(id)

  const dataSerialized = dataTyped?.length
    ? dataTyped.reduce(
        (acc, { failures, success, ...rest }) => {
          acc[0].success += success
          acc[0].failures += failures
          acc[0].total += success + failures
          acc[0]['400'] += rest['400s']
          acc[0]['500'] += rest['500s']
          return acc
        },
        [{ '400': 0, '500': 0, date: 'dummy', failures: 0, success: 0, total: 0 }] satisfies TransformedDataType
      )
    : [{ '400': 0, '500': 0, date: 'dummy', failures: 0, fake: 1, success: 0, total: 0 }]

  const CustomTooltip = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      ({ active, payload }: TooltipProps<ValueType, NameType>) => {
        if (active && payload?.length) {
          return (
            // TODO: Once recharts 3.0 is released, we can use the `portal` prop to render the tooltip in a portal, until then we need this hacky solution
            <PopperPortal>
              <TooltipContent payload={payload} />
            </PopperPortal>
          )
        }

        return null
      },
    [dataSerialized]
  )

  if (isLoading) {
    return (
      <div className='flex h-[34px] w-[182px] items-center'>
        <Loader className='h-4 w-full' pulse />
      </div>
    )
  }

  return (
    <>
      <div className='h-[34px] w-[182px]' data-testid='table-request-health'>
        <ResponsiveContainer>
          <BarChart data={dataSerialized} layout='vertical' margin={MARGIN} stackOffset='expand' width={BAR_WIDTH}>
            <YAxis dataKey='date' hide type='category' />
            <XAxis axisLine={false} cursor={undefined} hide type='number' />
            <Tooltip
              allowEscapeViewBox={{ x: true, y: true }}
              content={<CustomTooltip />}
              cursor={false}
              isAnimationActive={false}
            />
            {dataSerialized[0].total ? (
              <>
                <Bar
                  barSize={BAR_HEIGHT}
                  dataKey='success'
                  fill={green}
                  isAnimationActive={false}
                  minPointSize={BAR_WIDTH}
                  stackId='a'
                />
                <Bar
                  barSize={BAR_HEIGHT}
                  dataKey='failures'
                  fill={red}
                  isAnimationActive={false}
                  minPointSize={BAR_WIDTH}
                  stackId='a'
                />
              </>
            ) : (
              <Bar
                barSize={BAR_HEIGHT}
                dataKey='fake'
                fill={theme.extend.colors.gray['700']}
                isAnimationActive={false}
                minPointSize={BAR_WIDTH}
                stroke={theme.extend.colors.border.muted}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  )
}
